/**
 * LE TIROIR DE LA CARTE — la carte, ce qu'on écrit dessus, et ce que les autres y ont écrit.
 *
 * TROIS CHOSES FONT D'UNE CARTE UN OUTIL plutôt qu'une image, et elles sont toutes ici (§4.2) :
 *
 * 1. **On annote une case en la touchant.** On clique un hexagone, on choisit l'un des neuf
 *    marqueurs, on ajoute cent vingt signes si l'on veut. Le serveur refuse une case jamais vue —
 *    l'interface, elle, ne propose que les cases qu'elle a, ce qui revient au même sans faire
 *    l'aller-retour.
 * 2. **Les affaires en suspens**, la liste de ce qui n'est pas résolu. C'est le vrai instrument de
 *    navigation d'une aventure de mille nœuds : « la porte verrouillée du marais », trois heures
 *    plus tard, ne se retrouve pas de mémoire.
 * 3. **Le filtre**, qui n'affiche que ce qu'on cherche. Ce qu'il masque disparaît de la carte, pas
 *    de la liste : un filtre qui cache aussi la liste est un filtre qui perd des notes.
 *
 * ON LIT UNE NOTE EN PASSANT LA MAIN DESSUS, ET LA CASE RÉPOND (design §3). Un marqueur posé sur la
 * carte fait quinze pixels : il dit qu'il y a quelque chose d'écrit là, pas ce qui est écrit. Et
 * les listes rangeaient leurs notes sous une clef d'hexagone que rien, sur le dessin, ne montre —
 * on avait donc la note et la case sous les yeux sans aucun moyen de les relier. Deux chemins, et
 * aucun n'est un clic : la main passe sur une case, LE CARTOUCHE l'écrit ; la main passe sur une
 * note, LA CASE BAT. Le nom du lieu remplace la clef partout où la case a été visitée.
 *
 * LE CALQUE COMMUNAUTAIRE S'ÉTEINT D'UN BOUTON. Précieux la première fois qu'on entre dans une
 * région, encombrant ensuite : à trois cents notes sur quatre-vingts hexagones, il couvre le
 * dessin. Il se coupe entier, sans jamais toucher aux siennes.
 */
import { useMemo, useState } from "react";

import type { Annotation, CarteEnVue, HexagoneFrequente, LesAnnotations, Marqueur } from "../../api/types.js";
import { MARQUEURS } from "../../api/types.js";
import { ceQuiEstEcritSur, comptesParMarqueur, enSuspens, filtrees, riennEstEcrit } from "../../carte/annotations.js";
import { nomsDesCases } from "../../carte/hexagones.js";
import { CarteDuMonde } from "../../composants/CarteDuMonde.js";
import { Glyphe } from "../../composants/Glyphe.js";
import { Illustration } from "../../composants/Illustration.js";
import { CAPS, MARQUEURS_DITS } from "../../lexique/mots.js";
import { t, tn } from "../../lexique/langue.js";

const NOTE_MAXIMUM = 120;

/** Le formulaire d'une note : il n'apparaît qu'une fois une case choisie. Un formulaire toujours
 * ouvert au-dessus d'une carte est un formulaire qui prend la place de la carte. */
function PoserUneNote({
  ou,
  surAnnote,
  surRenonce
}: {
  /** Le NOM du lieu quand la case a été visitée, sa clef sinon. « Sur h_042 » ne dit rien à
   * personne — et l'on écrit sur un lieu, pas sur une ligne de base de données. */
  ou: string;
  surAnnote: (marqueur: Marqueur, note: string, publique: boolean) => void;
  surRenonce: () => void;
}) {
  const [marqueur, poseLeMarqueur] = useState<Marqueur>("revisiter");
  const [note, poseLaNote] = useState("");
  const [publique, posePublique] = useState(false);

  return (
    <form
      className="annoter"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        surAnnote(marqueur, note.trim(), publique);
        poseLaNote("");
      }}
    >
      <div className="rang rang--espace">
        <span className="capitales">{t("Sur {ou}", { ou })}</span>
        <button type="button" className="puce puce--sourde" onClick={surRenonce}>
          <Glyphe nom="croix" taille={12} /> {t("Renoncer")}
        </button>
      </div>

      <div className="annoter__marqueurs">
        {MARQUEURS.map((m) => (
          <button
            key={m}
            type="button"
            className={m === marqueur ? "puce puce--choisie" : "puce puce--sourde"}
            aria-pressed={m === marqueur}
            onClick={() => poseLeMarqueur(m)}
          >
            <Glyphe nom={MARQUEURS_DITS[m].glyphe} taille={14} />
            {MARQUEURS_DITS[m].nom}
          </button>
        ))}
      </div>

      <label className="annoter__note">
        <span className="capitales">{t("La note")}</span>
        <input
          type="text"
          className="champ champ--main"
          value={note}
          maxLength={NOTE_MAXIMUM}
          placeholder={t("cent vingt signes, pas un de plus")}
          onChange={(evenement) => poseLaNote(evenement.target.value)}
        />
        <span className="faible nombre">
          {note.length} / {NOTE_MAXIMUM}
        </span>
      </label>

      <label className="rang rang--serre">
        <input type="checkbox" checked={publique} onChange={(evenement) => posePublique(evenement.target.checked)} />
        <span>
          {t("La publier")} <span className="faible">{t("— les autres joueurs de cette aventure la verront, signée de votre héros")}</span>
        </span>
      </label>

      <button type="submit" className="bouton">
        {t("Écrire sur la carte")}
      </button>
    </form>
  );
}

/**
 * OÙ — le nom du lieu, et sa clef en petit derrière.
 *
 * `h_042` est ce que la base range et ce qu'un joueur cite quand il signale un bogue ; ce n'est pas
 * ce qu'il reconnaît en lisant sa carte. Le nom passe donc devant, la clef reste. Une case aperçue
 * de loin n'a pas de nom (§4.1) et l'on n'en invente pas : elle garde la clef seule.
 *
 * On peut cliquer : la case se choisit, comme si on l'avait cliquée sur la carte.
 */
function Ou({ clef, nom, surChoix }: { clef: string; nom: string | null; surChoix?: () => void }) {
  const dedans = (
    <>
      {nom ? <span className="capitales">{nom}</span> : null}
      <span className="nombre faible">{clef}</span>
    </>
  );
  if (!surChoix) return <span className="ou">{dedans}</span>;
  return (
    <button type="button" className="ou ou--prise" onClick={surChoix} title={t("Écrire sur {lieu}", { lieu: nom ?? clef })}>
      {dedans}
    </button>
  );
}

/**
 * LE CARTOUCHE — tout ce qui est écrit sur la case qu'on regarde.
 *
 * Il a une PLACE FIXE, sous la carte, et il ne suit pas le curseur : une bulle qui suit la souris
 * recouvre précisément la case qu'on est en train de regarder. Il ne disparaît pas non plus quand
 * la case est vide — « rien d'écrit ici » est une réponse, alors qu'un cartouche qui s'évanouit
 * ressemble à un survol qui n'a pas pris.
 *
 * Sur un écran tactile il n'y a pas de survol : c'est la case CHOISIE qui le remplit, et le
 * formulaire de note s'écrit alors juste dessous.
 */
function Cartouche({
  ecrit,
  survol
}: {
  ecrit: ReturnType<typeof ceQuiEstEcritSur>;
  survol: boolean;
}) {
  if (!ecrit) {
    return (
      <p className="cartouche cartouche--vide faible">
        {t("Passez la main sur une case : ce qui y est écrit s'affichera ici.")}
      </p>
    );
  }
  return (
    <div className={survol ? "cartouche" : "cartouche cartouche--tenue"}>
      <div className="rang rang--espace">
        <Ou clef={ecrit.clef} nom={ecrit.nom} />
        {!survol ? <span className="faible">{t("case choisie")}</span> : null}
      </div>
      {riennEstEcrit(ecrit) ? (
        <p className="faible" style={{ margin: 0 }}>
          {t("Rien d'écrit ici.")}
        </p>
      ) : (
        <ul className="cartouche__notes">
          {ecrit.miennes.map((note) => (
            <li key={note.id} className={note.resolue ? "cartouche__note cartouche__note--reglee" : "cartouche__note"}>
              <Glyphe nom={MARQUEURS_DITS[note.marqueur].glyphe} taille={14} />
              <span className="capitales">{MARQUEURS_DITS[note.marqueur].nom}</span>
              {note.note ? <span>{note.note}</span> : null}
              {note.resolue ? <span className="faible">réglé</span> : null}
            </li>
          ))}
          {/* CELLES DES AUTRES, EN ENCRE PÂLE ET SIGNÉES — la même « autre main » que sur la carte.
              Un joueur doit pouvoir se dire « ça, ce n'est pas moi qui l'ai écrit » sans y penser. */}
          {ecrit.desAutres.map((note) => (
            <li key={note.id} className="cartouche__note cartouche__note--calque">
              <Glyphe nom={MARQUEURS_DITS[note.marqueur].glyphe} taille={14} />
              <span className="capitales">{MARQUEURS_DITS[note.marqueur].nom}</span>
              {note.note ? <span>{note.note}</span> : null}
              <span className="faible">{note.heros ?? t("une autre main")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LigneEnSuspens({
  note,
  nom,
  surDesigne,
  surVaLa,
  surResout,
  surPublie,
  surEfface
}: {
  note: Annotation;
  nom: string | null;
  /** LA CASE BAT QUAND ON SURVOLE SA LIGNE. C'est le chemin inverse du cartouche, et c'est celui
   * qui manquait le plus : une clef écrite dans une liste ne se retrouve pas sur un dessin. */
  surDesigne: (clef: string | null) => void;
  surVaLa: (clef: string) => void;
  surResout: (id: string, resolue: boolean) => void;
  surPublie: (id: string, publique: boolean) => void;
  surEfface: (id: string) => void;
}) {
  const mot = MARQUEURS_DITS[note.marqueur];
  return (
    <li
      className={note.resolue ? "suspens suspens--reglee" : "suspens"}
      onMouseEnter={() => surDesigne(note.hexClef)}
      onMouseLeave={() => surDesigne(null)}
      // AU CLAVIER AUSSI : `onFocus` remonte depuis les boutons de la ligne (React le fait bouiller
      // jusqu'ici), donc parcourir la liste à la tabulation allume les cases une à une.
      onFocus={() => surDesigne(note.hexClef)}
      onBlur={() => surDesigne(null)}
    >
      <span className="suspens__quoi">
        <Glyphe nom={mot.glyphe} taille={14} />
        <span className="capitales">{mot.nom}</span>
        <Ou clef={note.hexClef} nom={nom} surChoix={() => surVaLa(note.hexClef)} />
      </span>
      {note.note ? <span className="suspens__note">{note.note}</span> : null}
      <span className="rang rang--serre">
        <button type="button" className="puce" onClick={() => surResout(note.id, !note.resolue)}>
          {note.resolue ? t("Rouvrir") : t("Réglé")}
        </button>
        <button type="button" className="puce puce--sourde" onClick={() => surPublie(note.id, !note.publique)}>
          {note.publique ? t("Retirer du calque") : t("Publier")}
        </button>
        <button type="button" className="puce puce--sourde" onClick={() => surEfface(note.id)} aria-label={t("Effacer")}>
          <Glyphe nom="croix" taille={12} />
        </button>
      </span>
    </li>
  );
}

export function LaCarteAnnotee({
  carte,
  annotations,
  frequentation = [],
  surAnnote,
  surResout,
  surPublie,
  surEfface,
  surVote
}: {
  carte: CarteEnVue | null;
  annotations: LesAnnotations;
  /** Le calque des morts (§4.4). Il s'éteint comme les deux autres. */
  frequentation?: HexagoneFrequente[];
  surAnnote: (hexagone: string, marqueur: Marqueur, note: string, publique: boolean) => void;
  surResout: (id: string, resolue: boolean) => void;
  surPublie: (id: string, publique: boolean) => void;
  surEfface: (id: string) => void;
  surVote: (id: string, pour: boolean) => void;
}) {
  const [choisi, poseLeChoisi] = useState<string | null>(null);
  // LA CASE SOUS LA MAIN, et LA CASE QU'UNE LIGNE DÉSIGNE : deux états, deux sens de lecture. Un
  // seul aurait fait battre la case qu'on survole sur la carte, ce qui n'apprend rien à personne.
  const [survole, poseLeSurvole] = useState<string | null>(null);
  const [designe, poseLeDesigne] = useState<string | null>(null);
  const [filtre, poseLeFiltre] = useState<Set<Marqueur>>(new Set());
  const [montreLeCalque, poseLeCalque] = useState(true);
  const [montreLesTombes, poseLesTombes] = useState(true);

  const miennes = useMemo(() => filtrees(annotations.miennes, filtre), [annotations.miennes, filtre]);
  const desAutres = useMemo(
    () => (montreLeCalque ? filtrees(annotations.calque, filtre) : []),
    [annotations.calque, filtre, montreLeCalque]
  );
  const suspens = useMemo(() => enSuspens(annotations.miennes), [annotations.miennes]);
  const comptes = useMemo(() => comptesParMarqueur(annotations.miennes), [annotations.miennes]);
  const noms = useMemo(() => nomsDesCases(carte?.hexagones ?? []), [carte]);
  // LE SURVOL PASSE DEVANT LA CASE CHOISIE, et la case choisie retient le cartouche quand la main
  // s'en va : sur un écran tactile, où il n'y a pas de survol, c'est le seul moyen de lire une
  // case — et c'est le même geste que celui qui ouvre le formulaire.
  const lu = survole ?? choisi;
  const ecrit = useMemo(() => ceQuiEstEcritSur(annotations, lu, noms), [annotations, lu, noms]);

  const bascule = (m: Marqueur) => {
    const suivant = new Set(filtre);
    if (suivant.has(m)) suivant.delete(m);
    else suivant.add(m);
    poseLeFiltre(suivant);
  };

  return (
    <>
      {carte?.lieu ? (
        <div className="lieu">
          <Illustration url={carte.lieu.illustration} legende={carte.lieu.nom ?? t("Le lieu")} forme="plan" />
          <p className="capitales" style={{ margin: 0 }}>
            {t("Vous êtes ici — {lieu}", { lieu: carte.lieu.nom ?? carte.lieu.region })}
          </p>
          {/* LE CAP, EN TOUTES LETTRES (§4.5). L'aiguille sur la rose se voit ; la phrase se lit, et
              c'est elle qu'on retient en fermant la carte. Elle ne dit rien de ce qu'il y a là-bas :
              une direction, un nombre de cases, et c'est tout ce que la boussole sait. */}
          {carte.boussole ? (
            <p className="faible" style={{ margin: 0 }}>
              {tn(
                carte.boussole.distance,
                "L'aiguille tient {cap} — à {n} case d'ici.",
                "L'aiguille tient {cap} — à {n} cases d'ici.",
                { cap: CAPS[carte.boussole.direction] ?? carte.boussole.direction }
              )}
            </p>
          ) : null}
        </div>
      ) : null}

      {carte ? (
        <CarteDuMonde
          carte={carte}
          hauteur={520}
          miennes={miennes}
          calque={desAutres}
          frequentation={montreLesTombes ? frequentation : []}
          choisi={choisi}
          designe={designe}
          surChoix={(clef) => poseLeChoisi(clef === choisi ? null : clef)}
          surSurvol={poseLeSurvole}
        />
      ) : (
        <p className="faible">{t("On la déplie…")}</p>
      )}

      {carte ? <Cartouche ecrit={ecrit} survol={survole !== null} /> : null}

      <p className="faible" style={{ marginBottom: 0 }}>
        {t("Noir : jamais vu. Pointillé : aperçu de loin — on n'en connaît que le repère. Dessiné : visité, avec son nom et son coût. Un toit marque un sanctuaire. On voyage par les chemins du texte, pas en cliquant sur la carte — un clic sur une case l'annote.")}
      </p>

      {/* LE TROISIÈME CALQUE S'ÉTEINT COMME LES DEUX AUTRES (§4.4). Il n'apparaît que s'il a
          quelque chose à dire : un interrupteur pour un calque vide est un interrupteur qui ment
          sur ce qu'il contient. */}
      {frequentation.some((h) => h.morts > 0) ? (
        <button
          type="button"
          className={montreLesTombes ? "puce puce--choisie" : "puce puce--sourde"}
          aria-pressed={montreLesTombes}
          onClick={() => poseLesTombes(!montreLesTombes)}
          style={{ marginTop: "var(--e3)" }}
        >
          <Glyphe nom="crane" taille={13} /> {t("Où d'autres sont tombés")}
        </button>
      ) : null}

      {choisi ? (
        <PoserUneNote
          ou={noms.get(choisi) ?? choisi}
          surRenonce={() => poseLeChoisi(null)}
          surAnnote={(marqueur, note, publique) => {
            surAnnote(choisi, marqueur, note, publique);
            poseLeChoisi(null);
          }}
        />
      ) : null}

      {annotations.miennes.length > 0 || annotations.calque.length > 0 ? (
        <>
          <div className="tiroir__soustitre capitales">{t("Ce qu'on affiche")}</div>
          <div className="annoter__marqueurs">
            {MARQUEURS.map((m) => (
              <button
                key={m}
                type="button"
                className={filtre.has(m) ? "puce puce--choisie" : "puce puce--sourde"}
                aria-pressed={filtre.has(m)}
                onClick={() => bascule(m)}
              >
                <Glyphe nom={MARQUEURS_DITS[m].glyphe} taille={13} />
                {MARQUEURS_DITS[m].nom}
                <span className="nombre faible">{comptes[m]}</span>
              </button>
            ))}
            {filtre.size > 0 ? (
              <button type="button" className="puce" onClick={() => poseLeFiltre(new Set())}>
                {t("Tout revoir")}
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      <div className="tiroir__soustitre capitales">{t("Affaires en suspens")}</div>
      <ul className="suspens-liste">
        {suspens.map((note) => (
          <LigneEnSuspens
            key={note.id}
            note={note}
            nom={noms.get(note.hexClef) ?? null}
            surDesigne={poseLeDesigne}
            surVaLa={poseLeChoisi}
            surResout={surResout}
            surPublie={surPublie}
            surEfface={surEfface}
          />
        ))}
        {!suspens.length ? (
          <li className="faible">
            {t("Rien en suspens. Une porte qu'on ne peut pas ouvrir, une bête qu'on a fuie, un coffre qu'on laisse : cliquez la case, et notez-le.")}
          </li>
        ) : null}
      </ul>

      {annotations.calque.length > 0 ? (
        <>
          <div className="rang rang--espace" style={{ marginTop: "var(--e4)" }}>
            <span className="tiroir__soustitre capitales" style={{ margin: 0 }}>
              {t("D'autres mains ({combien})", { combien: annotations.calque.length })}
            </span>
            <button type="button" className="puce puce--sourde" onClick={() => poseLeCalque(!montreLeCalque)}>
              {montreLeCalque ? t("Masquer le calque") : t("Montrer le calque")}
            </button>
          </div>
          <ul className="calque">
            {annotations.calque.map((note) => (
              <li
                key={note.id}
                onMouseEnter={() => poseLeDesigne(note.hexClef)}
                onMouseLeave={() => poseLeDesigne(null)}
                onFocus={() => poseLeDesigne(note.hexClef)}
                onBlur={() => poseLeDesigne(null)}
              >
                <span className="suspens__quoi">
                  <Glyphe nom={MARQUEURS_DITS[note.marqueur].glyphe} taille={14} />
                  <span className="capitales">{MARQUEURS_DITS[note.marqueur].nom}</span>
                  <Ou clef={note.hexClef} nom={noms.get(note.hexClef) ?? null} surChoix={() => poseLeChoisi(note.hexClef)} />
                </span>
                {note.note ? <span className="suspens__note">{note.note}</span> : null}
                <span className="rang rang--serre">
                  <span className="faible">{note.heros}</span>
                  <button
                    type="button"
                    className={note.votee ? "puce puce--choisie" : "puce puce--sourde"}
                    aria-pressed={note.votee === true}
                    onClick={() => surVote(note.id, !note.votee)}
                    title={note.votee ? t("Reprendre votre voix") : t("Cette note m'a servi")}
                  >
                    <Glyphe nom="plume" taille={12} /> <span className="nombre">{note.voix}</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}
